/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import { KpiTimePeriod, useBusinessContextKpiQuery } from "~/generated/graphql";
import { getKpiFetchConfig } from "~/components/KPI/utils";
import { KpiType } from "~/constants";
import { useCurrency } from "~/hooks";
import {
  mapBusinessContextKpi,
  mapKpiCurrentTarget,
  mapWorstOrgUnits,
} from "../mapping";

type Props = {
  bcId: string;
  period: KpiTimePeriod;
  kpiType: KpiType;
  skip: boolean;
  orgUnit?: string;
};

export function useKpiData({ bcId, period, kpiType, skip, orgUnit }: Props) {
  const { currency } = useCurrency();

  const {
    data: kpiData,
    loading: isKpiLoading,
    error: kpiError,
  } = useBusinessContextKpiQuery({
    variables: {
      bcId,
      timePeriod: period,
      ...getKpiFetchConfig(kpiType),
    },
    skip,
  });

  const mappedKpiData = React.useMemo(
    () => mapBusinessContextKpi(kpiType, kpiData),
    [kpiData, kpiType]
  );

  const mappedWorstOrgUnits = React.useMemo(
    () => mapWorstOrgUnits(currency, kpiData),
    [currency, kpiData]
  );
  const orgUnitName =
    mappedWorstOrgUnits?.find(({ nid }) => nid === orgUnit)?.name ?? "";

  const currentTarget = mapKpiCurrentTarget(kpiType, kpiData);

  return {
    mappedKpiData,
    mappedWorstOrgUnits,
    orgUnitName,
    currentTarget,
    isKpiLoading,
    kpiError,
  };
}
